/*
 * <heritage-print-sheet>
 *
 * Drives the one-page, print-only spec sheet on the PDP. Replaces the
 * old `onclick="window.print()"` that printed the entire ~27-page
 * document. Responsibilities:
 *
 *   1. Relocation — on connect, move this element to be a direct child
 *      of <body> so the print-isolation rule in the section stylesheet
 *      (`body > *:not(.heritage-print-sheet) { display: none }`) is
 *      deterministic regardless of how deeply the section nests it.
 *
 *   2. Printed-on date — the page HTML may be edge-cached, so a Liquid
 *      `'now'` would render stale. The date is stamped live instead.
 *
 *   3. Trigger wiring + sign-in gate — binds the icon-row Print button
 *      ([data-print-trigger]). The sheet carries wholesale pricing, so
 *      the action is gated: a signed-in partner prints immediately; an
 *      anonymous visitor is routed through login with a return_url
 *      (mirroring the add-to-cart gate in heritage-pdp-form.js) and the
 *      print fires automatically when they land back on the PDP.
 *
 * Signed-in state + the login route are read from the shared
 * window.__MDJ_INTEGRATIONS__ blob (set in layout/theme.liquid), the
 * same source the rest of the theme's customer-state branching uses.
 *
 * Required attribute:
 *   data-product-key — product id; scopes the post-login auto-print
 *                      intent so returning to a *different* PDP doesn't
 *                      fire a stale print.
 */

(function () {
  if (customElements.get('heritage-print-sheet')) return;

  // localStorage key + freshness window for the "print after login"
  // intent. Kept short so an abandoned gate can't silently fire a print
  // on some unrelated later visit.
  var INTENT_KEY = 'mdj.print_intent';
  var INTENT_TTL_MS = 15 * 60 * 1000;

  function integrations() {
    return window.__MDJ_INTEGRATIONS__ || {};
  }

  function isSignedIn() {
    var cfg = integrations();
    return !!(cfg.customer && cfg.customer.id);
  }

  function loginUrl() {
    var cfg = integrations();
    return (cfg.routes && cfg.routes.login) || '/account/login';
  }

  function readIntent() {
    try {
      var raw = window.localStorage.getItem(INTENT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeIntent(key) {
    try {
      window.localStorage.setItem(INTENT_KEY, JSON.stringify({ key: key, ts: Date.now() }));
    } catch (e) {
      /* private mode / quota — degrade to "no auto-print after login" */
    }
  }

  function clearIntent() {
    try {
      window.localStorage.removeItem(INTENT_KEY);
    } catch (e) {}
  }

  class HeritagePrintSheet extends HTMLElement {
    connectedCallback() {
      // appendChild below re-fires connectedCallback; guard re-init.
      if (this._ready) return;
      this._ready = true;

      this.productKey = this.getAttribute('data-product-key') || '';

      this.relocateToBody();
      this.setupPrintIsolation();
      this.stampDate();
      this.bindTriggers();
      this.maybeAutoPrint();
    }

    // Move the sheet to <body>. Removes any stale sheet for the same
    // product first (guards the theme editor re-rendering the section
    // while an already-relocated sheet lingers in <body>).
    relocateToBody() {
      if (this.parentElement === document.body) return;
      var key = this.productKey;
      var kids = document.body.children;
      for (var i = kids.length - 1; i >= 0; i--) {
        var el = kids[i];
        if (
          el !== this &&
          el.tagName === 'HERITAGE-PRINT-SHEET' &&
          el.getAttribute('data-product-key') === key
        ) {
          el.remove();
        }
      }
      document.body.appendChild(this);
    }

    // Belt-and-suspenders isolation. The inline @media print CSS already
    // hides every non-sheet body child, but that depends on the
    // `body > *:not(.heritage-print-sheet)` selector matching the live
    // DOM. This hides each real body child directly (inline
    // !important) at print time, so the sheet prints alone no matter
    // what the page structure is. Fires for BOTH the Print button and a
    // native Ctrl+P, and restores the page when the dialog closes.
    setupPrintIsolation() {
      var self = this;
      var apply = function () { self.togglePageHidden(true); };
      var restore = function () { self.togglePageHidden(false); };

      window.addEventListener('beforeprint', apply);
      window.addEventListener('afterprint', restore);

      // Safari lacks reliable beforeprint/afterprint; drive off the
      // print media query instead (also a restore safety net elsewhere).
      if (window.matchMedia) {
        try {
          var mql = window.matchMedia('print');
          var onChange = function (e) { (e.matches ? apply : restore)(); };
          if (mql.addEventListener) mql.addEventListener('change', onChange);
          else if (mql.addListener) mql.addListener(onChange);
        } catch (e) {}
      }
    }

    togglePageHidden(hidden) {
      var body = document.body;
      if (!body) return;
      var kids = body.children;
      for (var i = 0; i < kids.length; i++) {
        var el = kids[i];
        if (el === this) continue;
        var tag = el.tagName;
        if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'LINK' ||
            tag === 'TEMPLATE' || tag === 'NOSCRIPT') continue;

        if (hidden) {
          // Stash the element's own inline display (value + priority) so
          // modals etc. that rely on inline display are restored exactly.
          if (!el.hasAttribute('data-mdj-print-prev')) {
            var prev = el.style.getPropertyValue('display') + '|' +
                       el.style.getPropertyPriority('display');
            el.setAttribute('data-mdj-print-prev', prev);
            el.style.setProperty('display', 'none', 'important');
          }
        } else if (el.hasAttribute('data-mdj-print-prev')) {
          var saved = el.getAttribute('data-mdj-print-prev').split('|');
          el.style.removeProperty('display');
          if (saved[0]) el.style.setProperty('display', saved[0], saved[1] || '');
          el.removeAttribute('data-mdj-print-prev');
        }
      }
    }

    stampDate() {
      var el = this.querySelector('[data-print-date]');
      if (!el) return;
      try {
        el.textContent = new Date().toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } catch (e) {
        el.textContent = new Date().toDateString();
      }
    }

    bindTriggers() {
      var self = this;
      var triggers = document.querySelectorAll('[data-print-trigger]');
      Array.prototype.forEach.call(triggers, function (btn) {
        if (btn._printBound) return;
        btn._printBound = true;
        btn.addEventListener('click', function (event) {
          event.preventDefault();
          self.requestPrint();
        });
      });
    }

    requestPrint() {
      if (isSignedIn()) {
        this.printWhenReady();
        return;
      }
      // Gated: remember the intent, send to login with a return_url back
      // to this exact PDP, and let maybeAutoPrint() fire on return.
      writeIntent(this.productKey);
      var target = loginUrl();
      try {
        var u = new URL(target, location.origin);
        u.searchParams.set('return_url', location.pathname + location.search);
        location.href = u.pathname + u.search;
      } catch (e) {
        location.href = target;
      }
    }

    maybeAutoPrint() {
      if (!isSignedIn()) return;
      var intent = readIntent();
      if (!intent || intent.key !== this.productKey) return;
      if (!intent.ts || Date.now() - intent.ts > INTENT_TTL_MS) {
        clearIntent();
        return;
      }
      clearIntent();
      // Defer so the post-login navigation has settled (layout painted,
      // gated price now resolved) before opening the print dialog.
      var self = this;
      window.setTimeout(function () {
        self.printWhenReady();
      }, 350);
    }

    // Open the print dialog once the sheet's images have loaded, so the
    // product photo never prints blank. Falls back on a timeout if a
    // load/error event never arrives.
    printWhenReady() {
      var imgs = Array.prototype.slice.call(this.querySelectorAll('img'));
      var pending = imgs.filter(function (im) {
        return !im.complete;
      });

      var fired = false;
      function go() {
        if (fired) return;
        fired = true;
        try {
          window.print();
        } catch (e) {}
      }

      if (!pending.length) {
        window.requestAnimationFrame(go);
        return;
      }

      var remaining = pending.length;
      function done() {
        remaining -= 1;
        if (remaining <= 0) go();
      }
      pending.forEach(function (im) {
        im.addEventListener('load', done, { once: true });
        im.addEventListener('error', done, { once: true });
      });
      window.setTimeout(go, 1800);
    }
  }

  customElements.define('heritage-print-sheet', HeritagePrintSheet);
})();
